// CHAT-SWITCH-A: Slack's "Set a status" — the viewer's own row at the top of the chat sidebar
// (their status emoji and text, or "Set a status") opens a small modal: an emoji, a text (up to
// 100), "Clear after", quick presets, Save and Clear.
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmojiPickerPopover,
  Field,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  NavRow,
  toast,
} from "@companyos/ui";
import { SmileIcon, XIcon } from "lucide-react";
import { type FormEvent, useState } from "react";
import type { Api } from "../../api/client";
import type { ChatStatus } from "./chat-queries";
import { useMemberStatus, useSetStatus } from "./member-status";

export type ClearAfter = "never" | "30m" | "1h" | "4h" | "today" | "week";

export const CLEAR_AFTER: { value: ClearAfter; label: string }[] = [
  { value: "never", label: "Don't clear" },
  { value: "30m", label: "30 minutes" },
  { value: "1h", label: "1 hour" },
  { value: "4h", label: "4 hours" },
  { value: "today", label: "Today" },
  { value: "week", label: "This week" },
];

export const STATUS_PRESETS: { emoji: string; text: string; clear: ClearAfter }[] = [
  { emoji: "📅", text: "In a meeting", clear: "1h" },
  { emoji: "🚌", text: "Commuting", clear: "30m" },
  { emoji: "🤒", text: "Out sick", clear: "today" },
  { emoji: "🌴", text: "Vacationing", clear: "never" },
  { emoji: "🏠", text: "Working remotely", clear: "today" },
];

/** When a status set at `now` clears itself (null: until changed). Today/this week: local end. */
export function clearAt(choice: ClearAfter, now: Date): string | null {
  const plus = (ms: number) => new Date(now.getTime() + ms).toISOString();
  switch (choice) {
    case "30m":
      return plus(30 * 60_000);
    case "1h":
      return plus(60 * 60_000);
    case "4h":
      return plus(4 * 60 * 60_000);
    case "today": {
      const end = new Date(now);
      end.setHours(23, 59, 59, 999);
      return end.toISOString();
    }
    case "week": {
      // The week ends on Sunday night (Slack's "This week").
      const end = new Date(now);
      end.setDate(end.getDate() + ((7 - end.getDay()) % 7));
      end.setHours(23, 59, 59, 999);
      return end.toISOString();
    }
    default:
      return null;
  }
}

type Chat = { api: Api; co: string; meId: string | null; readOnly: boolean };

/** The viewer's own status row (the sidebar's top): opens the "Set a status" modal. */
export function MyStatusRow({ chat }: { chat: Chat }) {
  const mine = useMemberStatus(chat, chat.meId);
  const [open, setOpen] = useState(false);
  if (chat.meId === null || chat.readOnly) return null;
  const has = Boolean(mine?.emoji || mine?.text);
  return (
    <>
      <NavRow
        {...(has
          ? {
              lead: (
                <span aria-hidden="true" className="w-4 text-center text-[13px] leading-none">
                  {mine?.emoji ?? "💬"}
                </span>
              ),
            }
          : { icon: SmileIcon })}
        label={has ? (mine?.text ?? "Status set") : "Set a status"}
        className={`w-full text-left ${has ? "" : "text-muted-foreground"}`}
        data-slot="sidebar-my-status"
        title={has ? "Update your status" : "Set a status"}
        render={<button type="button" onClick={() => setOpen(true)} />}
      />
      {open ? <SetStatusDialog chat={chat} current={mine} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

export function SetStatusDialog({
  chat,
  current,
  onClose,
  now = () => new Date(),
}: {
  chat: Chat;
  current: ChatStatus | null;
  onClose: () => void;
  now?: () => Date;
}) {
  const [emoji, setEmoji] = useState(current?.emoji ?? "");
  const [text, setText] = useState(current?.text ?? "");
  const [clear, setClear] = useState<ClearAfter>("never");
  // An unchanged status keeps its own "until" unless another choice is made.
  const [touched, setTouched] = useState(false);
  const set = useSetStatus(chat);
  const has = Boolean(current?.emoji || current?.text);
  const save = (body: Parameters<typeof set.mutate>[0], done: string) =>
    set.mutate(body, {
      onSuccess: () => {
        toast.add({ title: done, type: "success" });
        onClose();
      },
      onError: (e) =>
        toast.add({ title: "Could not save your status", description: e.message, type: "error" }),
    });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const t = text.trim();
    const em = emoji.trim();
    if (!t && !em) {
      if (has) save({ emoji: null, text: null, expires_at: null }, "Status cleared");
      else onClose();
      return;
    }
    save(
      {
        emoji: em || null,
        text: t || null,
        expires_at: touched || !has ? clearAt(clear, now()) : (current?.expires_at ?? null),
      },
      "Status saved",
    );
  };
  const pick = (p: (typeof STATUS_PRESETS)[number]) => {
    setEmoji(p.emoji);
    setText(p.text);
    setClear(p.clear);
    setTouched(true);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !set.isPending && onClose()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Set a status</DialogTitle>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="status-text">Status</FieldLabel>
            <div className="flex items-center gap-1.5">
              <EmojiPickerPopover
                label={emoji ? `Status emoji ${emoji}: change` : "Pick an emoji"}
                align="start"
                onPick={(e) => {
                  setEmoji(e);
                  setTouched(true);
                }}
                trigger={
                  <Button
                    type="button"
                    variant="outline"
                    size="icon-sm"
                    aria-label={emoji ? `Status emoji ${emoji}: change` : "Pick an emoji"}
                  >
                    {emoji ? (
                      <span className="text-base leading-none">{emoji}</span>
                    ) : (
                      <SmileIcon />
                    )}
                  </Button>
                }
              />
              <Input
                id="status-text"
                value={text}
                maxLength={100}
                placeholder="What's your status?"
                autoFocus
                onChange={(e) => {
                  setText(e.target.value);
                  setTouched(true);
                }}
              />
              {emoji || text ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Empty the status"
                  title="Empty"
                  onClick={() => {
                    setEmoji("");
                    setText("");
                    setTouched(true);
                  }}
                >
                  <XIcon />
                </Button>
              ) : null}
            </div>
          </Field>
          <ul aria-label="Suggestions" className="m-0 flex list-none flex-col gap-px p-0">
            {STATUS_PRESETS.map((p) => (
              <li key={p.text}>
                <button
                  type="button"
                  onClick={() => pick(p)}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring"
                >
                  <span aria-hidden="true">{p.emoji}</span>
                  {p.text}
                  <span className="ml-auto text-xs text-muted-foreground">
                    {CLEAR_AFTER.find((c) => c.value === p.clear)?.label}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <Field>
            <FieldLabel htmlFor="status-clear">Clear after</FieldLabel>
            <NativeSelect
              id="status-clear"
              value={clear}
              onChange={(e) => {
                setClear(e.target.value as ClearAfter);
                setTouched(true);
              }}
            >
              {CLEAR_AFTER.map((c) => (
                <NativeSelectOption key={c.value} value={c.value}>
                  {c.label}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <DialogFooter>
            {has ? (
              <Button
                type="button"
                variant="ghost"
                className="sm:mr-auto"
                disabled={set.isPending}
                onClick={() =>
                  save({ emoji: null, text: null, expires_at: null }, "Status cleared")
                }
              >
                Clear status
              </Button>
            ) : null}
            <DialogClose render={<Button variant="ghost" type="button" />}>Cancel</DialogClose>
            <Button
              type="submit"
              disabled={set.isPending || (!emoji.trim() && !text.trim() && !has)}
            >
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
